import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Complaint, State } from '@icc/b2b/shared';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import { ComplaintService } from '../complaint.service';
import { SyncService, getSelectedComplaint } from '@icc/b2b/sales-document';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { logger } from '@icc/shared';
import { NgxPermissionsService } from 'ngx-permissions';

@Component({
    selector: 'icc-complaint-correction-dialog',
    templateUrl: './complaint-correction-dialog.component.html',
    styleUrls: ['./complaint-correction-dialog.component.scss'],
})
export class ComplaintCorrectionDialogComponent implements OnInit, OnDestroy {

    /**
     * When true, the dialog is blocked because external changes were detected.
     */
    isBlocked = false;

    /**
     * Flag indicating that a permission-affecting change was detected externally.
     * When true, the next store update will trigger a blocking check.
     */
    private pendingBlockCheck = false;

    /**
     * Timestamp when the dialog was opened.
     */
    private dialogOpenedAt = 0;

    /**
     * Cached value of whether user has manufacturer permission.
     * Loaded once on init since permissions don't change during dialog lifetime.
     */
    private hasManufacturerPermission = false;

    private subscriptions: Subscription[] = [];

    correctionNumber: string | null = null;
    discount: number;
    private _rawDiscountInput = '';

    constructor(
        private dialogRef: DialogRef,
        @Inject(DIALOG_DATA)
        public data: {
            complaint: Complaint,
        },
        private complaintService: ComplaintService,
        private syncService: SyncService,
        private store: Store<State>,
        private permissionsService: NgxPermissionsService,
        private cdr: ChangeDetectorRef,
    ) {
        this.correctionNumber = this.data.complaint.correctionNumber ?? null;
        this.discount = this.data.complaint.discount ?? 0;
        this._rawDiscountInput = this.discount.toString();
    }

    async ngOnInit(): Promise<void> {
        this.dialogOpenedAt = Date.now();

        // Cache manufacturer permission - doesn't change during dialog lifetime
        this.hasManufacturerPermission = await this.permissionsService.hasPermission('complaints-manage-manufacturer');

        this.setupExternalChangesDetection();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    /**
     * Sets up detection of external changes.
     * Blocks the dialog only if an external change causes loss of edit permission.
     */
    private setupExternalChangesDetection(): void {
        // Fields that affect edit permission
        const permissionFields = [
            'assignedTo',
            'distributorStatusId',
            'manufacturerStatusId',
            'authorizedRetailerStatusId',
        ];

        this.subscriptions.push(
            // Subscribe to external changes - set flag when permission-affecting changes detected
            this.syncService.externalChanges$.subscribe(({
                documentIds,
                documentFields,
                timestamp
            }) => {
                // Ignore changes that happened before dialog was opened
                if (timestamp <= this.dialogOpenedAt) {
                    return;
                }

                // Check if this complaint was modified
                if (!documentIds.has(this.data.complaint.uuid)) {
                    return;
                }

                // If permission-affecting field changed, check immediately
                if (permissionFields.some(field => documentFields.has(field))) {
                    logger.log('[ComplaintCorrectionDialog] Permission field changed externally');
                    this.pendingBlockCheck = true;
                    // Check immediately with current data
                    if (!this.currentStatusAllowEdit() || !this.isUserAssigned()) {
                        logger.log('[ComplaintCorrectionDialog] BLOCKING - external change removed permission');
                        this.isBlocked = true;
                        this.cdr.detectChanges();
                    }
                }
            }),

            // Subscribe to complaint document changes - update data and check blocking if needed
            this.store.select(getSelectedComplaint).subscribe((complaint) => {
                if (complaint && complaint.uuid === this.data.complaint.uuid) {
                    Object.assign(this.data.complaint, complaint);

                    // If we have a pending block check, perform it now with updated data
                    if (this.pendingBlockCheck && !this.isBlocked) {
                        if (!this.currentStatusAllowEdit() || !this.isUserAssigned()) {
                            logger.log('[ComplaintCorrectionDialog] BLOCKING - external change removed permission');
                            this.isBlocked = true;
                            this.cdr.detectChanges();
                        }
                    }
                }
            }),
        );
    }

    /**
     * Checks if the current status allows editing.
     */
    private currentStatusAllowEdit(): boolean {
        const userType = this.complaintService.getUserType();
        const statusId = this.complaintService.getDocumentStatusForUser(this.data.complaint, userType);

        if (statusId === 0 || isNaN(statusId)) {
            return true;
        }

        const status = this.complaintService.statuses.find((s) => s.id === statusId);
        return status?.allowEdit ?? false;
    }

    /**
     * Checks if the current user is the assigned party or has manufacturer permission.
     * Users with 'complaints-manage-manufacturer' permission bypass the assignment check.
     */
    private isUserAssigned(): boolean {
        if (this.hasManufacturerPermission) {
            return true;
        }
        const userType = this.complaintService.getUserType();
        return this.data.complaint.assignedTo?.startsWith(userType) ?? false;
    }

    get isDiscountValid(): boolean {
        if (this.discount < 0) {
            return false;
        }

        const decimalStr = this._rawDiscountInput.includes('.') ? this._rawDiscountInput.split('.')[1] : '';
        return decimalStr.length <= 2;
    }

    onDiscountInputChange(value: number | string) {
        if (typeof value === 'string') {
            this._rawDiscountInput = value;
        } else {
            this._rawDiscountInput = value.toString();
        }

        this.discount = typeof value === 'number' ? value : parseFloat(value) || 0;
    }

    save() {
        if (!this.isDiscountValid) {
            return;
        }
        if (this.discount !== this.data.complaint.discount) {
            this.complaintService.changeDiscount(this.data.complaint, this.discount);
        }
        if (this.correctionNumber !== this.data.complaint.correctionNumber) {
            this.complaintService.changeCorrectionNumber(this.data.complaint, this.correctionNumber);
        }
        this.dialogRef.close();
    }

    close() {
        this.dialogRef.close();
    }
}
